import {createPaginationContainer, graphql} from 'react-relay';
import {injectIntl} from 'react-intl';
import {withRouter} from 'react-router-dom';
import {getAggregatedResourceNumbersByMonth, mapCompressedAggregatedResourceNumbers} from '../UtilizationReportUtils';

const UtilizationReportMonthByMonthDepartmentLoader = ({relay: {hasMore, isLoading, loadMore}, viewer, children, ...props}) => {
	const formatData = () => {
		return viewer.company.resourceBasedDepartments.edges
			.map(edge => edge.node)
			.map(department => {
				const {id, name} = department;
				const aggregatedResourceNumbers = mapCompressedAggregatedResourceNumbers(department.aggregatedResourceNumbers, [
					'forecastBillableUtilization',
					'forecastBillableUtilizationWin',
					'forecastBillableUtilizationSoft',
					'forecastBillableUtilizationSoftWin',
					'forecastBillableUtilizationHard',
					'forecastResourceUtilization',
					'forecastResourceUtilizationWin',
					'forecastResourceUtilizationSoft',
					'forecastResourceUtilizationSoftWin',
					'forecastResourceUtilizationHard',
					'forecastBillableUtilizationTaskAndAllocationsCombined',
					'forecastBillableUtilizationTaskAndAllocationsCombinedWin',
					'forecastBillableUtilizationTaskAndAllocationsCombinedSoft',
					'forecastBillableUtilizationTaskAndAllocationsCombinedSoftWin',
					'forecastBillableUtilizationTaskAndAllocationsCombinedHard',
					'forecastResourceUtilizationTaskAndAllocationsCombined',
					'forecastResourceUtilizationTaskAndAllocationsCombinedWin',
					'forecastResourceUtilizationTaskAndAllocationsCombinedSoft',
					'forecastResourceUtilizationTaskAndAllocationsCombinedSoftWin',
					'forecastResourceUtilizationTaskAndAllocationsCombinedHard',
				]);
				const aggregatedResourceNumbersByMonth = getAggregatedResourceNumbersByMonth(aggregatedResourceNumbers);
				return {
					department: {
						id,
						name,
					},
					aggregatedResourceNumbersByMonth,
				};
			});
	};

	const loadMoreFunc = hasMore() ? loadMore : undefined;
	return children({data: {rows: formatData()}, loadMore: loadMoreFunc, loading: isLoading(), ...props});
};

export const utilizationReportMonthByMonthDepartmentLoaderQuery = graphql`
	query UtilizationReportMonthByMonthDepartmentLoaderQuery_Query(
		$searchQuery: TaskSearchQueryType
		$startYear: Int!
		$startMonth: Int!
		$startDay: Int!
		$endYear: Int!
		$endMonth: Int!
		$endDay: Int!
		$pageSize: Int
		$cursor: String
	) {
		viewer {
			actualPersonId
			component(name: "utilization_monthbymonth_department_loader")
			...UtilizationReportMonthByMonthDepartmentLoader_viewer
				@arguments(
					searchQuery: $searchQuery
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					pageSize: $pageSize
					cursor: $cursor
				)
		}
	}
`;

export default injectIntl(
	withRouter(
		createPaginationContainer(
			UtilizationReportMonthByMonthDepartmentLoader,
			{
				viewer: graphql`
					fragment UtilizationReportMonthByMonthDepartmentLoader_viewer on Viewer
					@argumentDefinitions(
						searchQuery: {type: "TaskSearchQueryType"}
						startYear: {type: "Int"}
						startMonth: {type: "Int"}
						startDay: {type: "Int"}
						endYear: {type: "Int"}
						endMonth: {type: "Int"}
						endDay: {type: "Int"}
						pageSize: {type: "Int"}
						cursor: {type: "String"}
					) {
						company {
							resourceBasedDepartments(
								first: $pageSize
								after: $cursor
								searchQuery: $searchQuery
								startYear: $startYear
								startMonth: $startMonth
								startDay: $startDay
								endYear: $endYear
								endMonth: $endMonth
								endDay: $endDay
								aggregateLevel: MONTH
								aggregates: [
									"forecastBillableUtilization"
									"forecastBillableUtilizationWin"
									"forecastBillableUtilizationSoft"
									"forecastBillableUtilizationSoftWin"
									"forecastBillableUtilizationHard"
									"forecastResourceUtilization"
									"forecastResourceUtilizationWin"
									"forecastResourceUtilizationSoft"
									"forecastResourceUtilizationSoftWin"
									"forecastResourceUtilizationHard"
									"forecastBillableUtilizationTaskAndAllocationsCombined"
									"forecastBillableUtilizationTaskAndAllocationsCombinedWin"
									"forecastBillableUtilizationTaskAndAllocationsCombinedSoft"
									"forecastBillableUtilizationTaskAndAllocationsCombinedSoftWin"
									"forecastBillableUtilizationTaskAndAllocationsCombinedHard"
									"forecastResourceUtilizationTaskAndAllocationsCombined"
									"forecastResourceUtilizationTaskAndAllocationsCombinedWin"
									"forecastResourceUtilizationTaskAndAllocationsCombinedSoft"
									"forecastResourceUtilizationTaskAndAllocationsCombinedSoftWin"
									"forecastResourceUtilizationTaskAndAllocationsCombinedHard"
								]
							) @connection(key: "Company_resourceBasedDepartments") {
								edges {
									node {
										id
										name
										aggregatedResourceNumbers
									}
								}
							}
						}
					}
				`,
			},
			{
				direction: 'forward',
				getConnectionFromProps({viewer}) {
					return viewer.company.resourceBasedDepartments;
				},
				getFragmentVariables(prevVars) {
					return {
						...prevVars,
					};
				},
				getVariables(_, {cursor}, fragmentVariables) {
					return {
						...fragmentVariables,
						cursor,
					};
				},
				query: graphql`
					query UtilizationReportMonthByMonthDepartmentLoaderRefetchQuery(
						$pageSize: Int
						$cursor: String
						$startYear: Int
						$startMonth: Int
						$startDay: Int
						$endYear: Int
						$endMonth: Int
						$endDay: Int
						$searchQuery: TaskSearchQueryType
					) {
						viewer {
							...UtilizationReportMonthByMonthDepartmentLoader_viewer
								@arguments(
									pageSize: $pageSize
									cursor: $cursor
									startYear: $startYear
									startMonth: $startMonth
									startDay: $startDay
									endYear: $endYear
									endMonth: $endMonth
									endDay: $endDay
									searchQuery: $searchQuery
								)
						}
					}
				`,
			}
		)
	)
);
